import React, { useEffect, useMemo, useState } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import {
  Home,
  Users,
  Calendar,
  Settings,
  LogOut,
  Menu,
  X,
  Gavel,
  Bell,
  Shield,
  BarChart3,
  User,
  Tag,
  Building2,
  LayoutTemplate
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useNotifications } from '../../../contexts/NotificationContext';
import './AdminDashboard.css';
// Admin Views
import AdminHome from '../AdminHome/AdminHome';
import ManageUsers from '../ManageUsers/ManageUsers';
import ManageAuctions from '../ManageAuctions/ManageAuctions';
import AdminReports from '../AdminReports/AdminReports';
import ManagePricing from '../ManagePricing/ManagePricing';
import ManageLandingPage from '../ManageLandingPage/ManageLandingPage';
import ManageChannelPartners from '../ManageChannelPartners/ManageChannelPartners';
import SystemSettings from '../SystemSettings/SystemSettings';
// Auctioneer Components (Admin can access)
import AuctioneerLiveView from '../../Auctioneer/LiveAuction/AuctioneerLiveView';

const AdminDashboard: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : false
  );

  const { user, logout } = useAuth();
  const adminName = (user?.name || 'Admin User').trim();
  const adminInitials =
    adminName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase())
      .join('') || 'A';
  const { unreadCount } = useNotifications();
  const location = useLocation();

  const navigation = useMemo(
    () => [
      { name: 'Dashboard', href: '/admin', icon: Home, section: 'Overview' },
      { name: 'Manage Users', href: '/admin/users', icon: Users, section: 'Management' },
      { name: 'Manage Auctions', href: '/admin/auctions', icon: Gavel, section: 'Management' },
      { name: 'Reports & Analytics', href: '/admin/reports', icon: BarChart3, section: 'Business' },
      { name: 'Manage Pricing', href: '/admin/pricing', icon: Tag, section: 'Business' },
      { name: 'Landing Page', href: '/admin/landing-page', icon: LayoutTemplate, section: 'Business' },
      { name: 'Channel Partners', href: '/admin/channel-partners', icon: Building2, section: 'Business' },
      { name: 'System Settings', href: '/admin/settings', icon: Settings, section: 'System' },
    ],
    []
  );

  const navSections = useMemo(() => {
    const groups: { label: string; items: typeof navigation }[] = [];
    navigation.forEach((item) => {
      const group = groups.find((g) => g.label === item.section);
      if (group) group.items.push(item);
      else groups.push({ label: item.section, items: [item] });
    });
    return groups;
  }, [navigation]);

  const currentNavItem = navigation.find(
    (nav) =>
      location.pathname === nav.href ||
      (nav.href !== '/admin' && location.pathname.startsWith(nav.href))
  );

  // These pages render their own <h1>, so the header title would show the name twice.
  const pageHasOwnHeading =
    currentNavItem?.href === '/admin' ||
    currentNavItem?.href === '/admin/users' ||
    currentNavItem?.href === '/admin/auctions' ||
    currentNavItem?.href === '/admin/reports' ||
    currentNavItem?.href === '/admin/pricing' ||
    currentNavItem?.href === '/admin/channel-partners' ||
    currentNavItem?.href === '/admin/settings' ||
    currentNavItem?.href === '/admin/landing-page';

  // Sync sidebar state with viewport changes
  useEffect(() => {
    const onResize = () => {
      const nowDesktop = window.innerWidth >= 1024;
      setIsDesktop(nowDesktop);
      if (nowDesktop) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Close sidebar on route change (for mobile)
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await Promise.resolve(logout());
    } catch {
      // ignored
    }
  };

  return (
    <div className={`admin-dashboard ${isSidebarOpen ? 'sidebar-open' : ''}`}>
      {/* Mobile sidebar backdrop */}
      {isSidebarOpen && (
        <div
          className={`admin-sidebar-backdrop ${isSidebarOpen ? 'open' : ''}`}
          onClick={() => setIsSidebarOpen(false)}
          aria-hidden={!isSidebarOpen}
        />
      )}

      {/* Sidebar */}
      <aside
        id="admin-sidebar"
        className={`admin-sidebar ${isSidebarOpen ? 'open' : ''}`}
        aria-hidden={!isSidebarOpen && !isDesktop}
      >
        <div className="admin-sidebar-header">
          <div className="admin-logo">
            <div className="admin-logo-icon-wrapper">
              <Shield className="admin-logo-icon" />
            </div>
            <div className="admin-logo-copy">
              <span className="admin-logo-text">Admin Panel</span>
              <span className="admin-logo-sub">EasyEAuction</span>
            </div>
          </div>
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="admin-sidebar-close"
            aria-label="Close sidebar"
            type="button"
          >
            <X className="admin-icon" />
          </button>
        </div>

        <nav className="admin-nav" role="navigation" aria-label="Admin main navigation">
          {navSections.map((section) => (
            <div className="admin-nav-section" key={section.label}>
              <div className="admin-nav-section-label">{section.label}</div>
              {section.items.map((item) => {
                const Icon = item.icon;
                const isActive =
                  location.pathname === item.href ||
                  (item.href !== '/admin' && location.pathname.startsWith(item.href));

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`admin-nav-item ${isActive ? 'active' : ''}`}
                    onClick={() => setIsSidebarOpen(false)}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <span className="admin-nav-icon-wrap">
                      <Icon className="admin-nav-icon" />
                    </span>
                    <span className="admin-nav-label">{item.name}</span>
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {/* ─── Sidebar Footer (patched) ─── */}
<div className="admin-sidebar-footer">
  <div className="admin-user-card">
    <div className="admin-avatar" aria-hidden="true">{adminInitials}</div>
    <div className="admin-user-details">
      <div className="admin-user-name">{adminName}</div>
      <div className="admin-user-role">System Administrator</div>
      {user?.phoneNumber && (
        <div className="admin-user-phone">
          {user.phoneNumber.startsWith('+91')
            ? user.phoneNumber.replace('+91', '+91\u00A0')
            : user.phoneNumber}
        </div>
      )}
    </div>
  </div>

  <button onClick={handleLogout} className="admin-logout-btn" type="button">
    <LogOut className="admin-logout-icon" />
    Sign out
  </button>
</div>
      </aside>

      {/* Main content */}
      <div className="admin-main-content" role="region" aria-label="Admin main content">
        {/* Top header */}
        <header className="admin-header">
          <div className="admin-header-content">
            <button
              onClick={() => setIsSidebarOpen((o) => !o)}
              className="admin-mobile-menu"
              aria-label={isSidebarOpen ? 'Close sidebar' : 'Open sidebar'}
              aria-expanded={isSidebarOpen}
              aria-controls="admin-sidebar"
              type="button"
            >
              {isSidebarOpen ? <X className="admin-icon" /> : <Menu className="admin-icon" />}
            </button>

            <div className="admin-header-title">
              {!pageHasOwnHeading && (
                <h1 className="admin-page-title">{currentNavItem?.name || 'Admin Dashboard'}</h1>
              )}
            </div>

            <div className="admin-header-actions">
              {/* Notifications */}
              {/* <button
                className="admin-notification-btn"
                aria-label={`Notifications: ${unreadCount || 0} unread`}
                type="button"
              >
                <Bell className="admin-notification-icon" />
                {unreadCount > 0 && (
                  <span className="admin-notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
                )}
              </button> */}

            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="admin-content">
          <Routes>
            <Route index element={<AdminHome />} />
            <Route path="users" element={<ManageUsers />} />
            <Route path="auctions" element={<ManageAuctions />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="pricing" element={<ManagePricing />} />
            <Route path="landing-page" element={<ManageLandingPage />} />
            <Route path="channel-partners" element={<ManageChannelPartners />} />
            <Route path="settings" element={<SystemSettings />} />
            {/* Live Auction View - Admin can access as observer */}
            <Route path="auction-live/:id" element={<AuctioneerLiveView />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;

